import { observable } from 'mobx';
import { Native } from '@hb/hbridge';
import { BusinessError } from '../utils/Exception';
import { WalletService } from '../services';

interface IUserInfo {
  token?: string;
  userGuid?: string | void;
  mobile?: string | void;
  key?: string;
  guid?: string | void;
  ticket?: string;
}

interface IGeoLocation {
  cityCode?: string;
  adCode?: string;
  accurateLongitude?: string; // 精度
  accurateLatitude?: string; // 纬度
  province?: string; // 省
  towncode?: string;
  township?: string; // 镇
  locateFailMsg?: string; // 定位失败消息
  district?: string; // 区
  conntry?: string; // 国家
}

interface IEvBikeFundsInfo {
  zmxyFreeScore?: number; // 芝麻信用分
  hasFreeApply?: boolean; // 是否有免押
}

export default class MainStore {
  @observable public userInfo: IUserInfo = {
    guid: '',
    key: '',
    mobile: '',
    ticket: '',
    token: '',
  };

  private accountWallet: any = null;

  private evBikeFundsInfoPromise: Promise<IEvBikeFundsInfo> | null = null;

  private accountBasic: any;

  private evEikeMonthCardInfo: any;

  /**
   * 获取登陆信息
   */
  async getUserInfo(): Promise<IUserInfo> {
    const { token } = this.userInfo;
    if (!token) {
      return Native.getTokenAndKey({ needLogin: true }).then((data) => {
        this.userInfo = data;
        return data;
      });
    }
    return this.getUserInfo();
  }

  private locateResult = null;

  private geoLocation: IGeoLocation | null = null;

  /**
   * 获取逆地址编码
   */
  async regeocode(force = false, params: any = {}): Promise<IGeoLocation> {
    if (!force && this.geoLocation) {
      return this.geoLocation;
    }
    try {
      const res = await Native.callNative({
        method: 'getLocation',
        params: { showDialog: false, ...params },
      });
      if (res.locateFailMsg) {
        return {
          province: '萨嘎',
          cityCode: '0892',
          adCode: '540236',
          towncode: '540236100000',
          locateFailMsg: res.locateFailMsg,
        };
      }
      const data = await Native.callNative({
        method: 'regeocode',
        params: {
          latitude: res.latitude,
          longitude: res.longitude,
          extensions: 'all',
        },
      });
      if (data.addressComponent) {
        const { citycode, adcode } = data.addressComponent;
        data.addressComponent.cityCode = citycode;
        data.addressComponent.adCode = adcode;
        data.addressComponent.isHitPositionCache = 0;
        // 如果服务端需要真实的用户城市信息，可以用accurateCityCode 与 accurateAdCode，服务端必须兼容数值为空的情况
        // 用于区分原有的默认北京的cityCode和adCode
        data.addressComponent.accurateCityCode = citycode;
        data.addressComponent.accurateAdCode = adcode;
        data.addressComponent.formattedAddress = data.formatted_address;

        this.geoLocation = data.addressComponent;
        // try {
        //   Storage.setItem('last-position', {
        //     cityCode: citycode,
        //     adCode: adcode,
        //   });
        // } catch (e) {
        //   // empty
        // }
      }
      return data.addressComponent;
    } catch (e) {
      console.error(e);
      return Promise.reject(new BusinessError('获取位置信息失败!'));
    }
  }

  /**
   * 获取钱包信息
   */
  async getAccountWalletInfo(force = false): Promise<any> {
    if (!this.accountWallet || force) {
      try {
        const loc = await this.regeocode();
        const data = await WalletService.getAccountWalletInfo(
          loc.locateFailMsg ? {} : loc,
        );
        this.accountWallet = data;
      } catch (e) {
        this.accountWallet = null;
        return Promise.reject(e);
      }
    }
    return this.accountWallet;
  }

  /**
   * 获取助力车钱包信息
   */
  async getEvBikeFundsInfo(force = false): Promise<IEvBikeFundsInfo> {
    if (!this.evBikeFundsInfoPromise || force) {
      try {
        const loc = await this.regeocode();
        const data = await WalletService.getEvBikeFundsInfo(
          loc.locateFailMsg ? {} : loc,
        );
        this.evBikeFundsInfoPromise = Promise.resolve(data);
      } catch (e) {
        this.evBikeFundsInfoPromise = null;
        return Promise.reject(e);
      }
    }
    return this.evBikeFundsInfoPromise;
  }

  /**
   * 获取账号基础信息
   */
  async getAccountBasicInfo(update = false): Promise<any> {
    if (!this.accountBasic || update) {
      try {
        const loc = await this.regeocode();
        const data = await WalletService.getAccountBasicInfo(
          loc.locateFailMsg ? {} : loc,
        );
        this.accountBasic = data;
      } catch (e) {
        this.accountBasic = null;
        return Promise.reject(e);
      }
    }
    return this.accountBasic;
  }

  async getEvEikeMonthCardInfo(params: {
    force?: boolean;
    needCardRule?: boolean;
    cardType: number;
  }): Promise<any> {
    const { force, needCardRule, cardType } = params;
    if (!this.evEikeMonthCardInfo || force) {
      return this.regeocode().then(({ cityCode, adCode }) =>
        WalletService.getEvBikeMonthCardInfo({
          cityCode,
          adCode,
          needCardRule,
          cardType,
        })
          .then((data: any) => {
            this.evEikeMonthCardInfo = data;
            return this.evEikeMonthCardInfo;
          })
          .catch(() => {
            this.evEikeMonthCardInfo = null;
          }),
      );
    }
    return this.evEikeMonthCardInfo;
  }

  clearEvEikeMonthCardInfo() {
    this.evEikeMonthCardInfo = null;
  }
}
